import { React, useEffect, useState } from 'react';

import { Autocomplete, FormHelperText, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import YardRoundedIcon from '@mui/icons-material/YardRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/vegetationUpdate.css';

const AtualizarVegetacao = () => {
    const [dados, setDados] = useState([]);
    const [site, setSite] = useState('');
    const [listaSites, setListaSites] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [listaUsinas, setListaUsinas] = useState([]);
    const [statusVeg, setStatusVeg] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const listStatus = ['BAIXA', 'MÉDIA', 'ALTA'];

    useEffect(() => {
        async function buscarUsinasVegetacao () {
            const response = await fetchFlask('getUsinasVegetacao');
            if (response.status !== 'error') {
                const dadosFetch = response.dados;
                setDados(dadosFetch);
                const dadosSite = [...new Set(dadosFetch.map(item => item[0]))];
                setListaSites(dadosSite);
            } else {
                handleAlerta(response);
            }
        };
        buscarUsinasVegetacao();
    }, []);

    const siteSelect = (e) => {
        setSite(e.target.value);
        setUsinas([]);

        const dadosFiltrados = dados.filter(item => item[0] === e.target.value);

        const dadosUsinas = [...new Set(dadosFiltrados.map(item => item[1]))];
        dadosUsinas.sort();
        dadosUsinas.unshift('Selecionar Todas');
        setListaUsinas(dadosUsinas);
        setUsinas([]);
    };

    const usinasChange = (e, values) => {
        if (values.includes('Selecionar Todas') && !usinas.includes('Selecionar Todas')) {
            let todasUsinas = [];
            todasUsinas = todasUsinas.concat(listaUsinas);
            todasUsinas.splice(0, 1);
            setUsinas(todasUsinas);
        } else {
            setUsinas(values);
        }
    };

    async function submitAtualizarVegetacao (e) {
        e.preventDefault();
        setIsSubmit(true);

        if (!dateTime) {
            setError(true);
            setIsSubmit(false);
            return;
        }
        setError(false);
        const data = formatIsoDate(dateTime);

        const body = JSON.stringify({
            usina: usinas,
            statusVegetacao: statusVeg,
            dataVerificacao: data
        });

        try {
            const response = await fetchFlask('atualizarStatusVegetacao', body);
            handleAlerta(response);
            if (response.status === 'success') {
                setSite('');
                setUsinas([]);
                setStatusVeg('');
                setDateTime(null);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const dataLabel = formatDateLabelPtBr(dateTime);
    const usinasLabel = usinas.length > 0 ? `${usinas.length} selecionada${usinas.length > 1 ? 's' : ''}` : 'Nenhuma selecionada';
    const completedFields = [
        site,
        usinas.length,
        statusVeg,
        dateTime
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Atualizar Vegetação</PageHeader>

            <form method='POST' id='formAtualizarVegetacao' onSubmit={submitAtualizarVegetacao} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Monitoramento de vegetação das usinas</h2>
                        </Box>
                        <p>
                            Registre o nível de vegetação identificado na vistoria e vincule a atualização às usinas selecionadas.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ApartmentRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Site e usinas</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelSite'>Site</InputLabel>
                                <Select
                                    id='selectSite'
                                    labelId='labelSite'
                                    label='Site'
                                    value={site}
                                    onChange={siteSelect}
                                >
                                    {listaSites.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Autocomplete
                                className='vegetation-update-autocomplete'
                                multiple
                                disableCloseOnSelect
                                id='usinas'
                                limitTags={4}
                                value={usinas}
                                options={listaUsinas}
                                onChange={usinasChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Usinas'
                                        required={usinas.length === 0}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <YardRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Status da vegetação</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelStatusVeg'>Status Vegetação</InputLabel>
                                <Select
                                    id='statusVeg'
                                    labelId='labelStatusVeg'
                                    label='Status Vegetação'
                                    value={statusVeg}
                                    onChange={(e) => setStatusVeg(e.target.value)}
                                >
                                    {listStatus.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl className='ui-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Data Verificação'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Box className='vegetation-update-status-scale'>
                            {listStatus.map((item) => (
                                <span key={item} className={statusVeg === item ? 'is-active' : undefined}>{item}</span>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da vistoria</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Site</span>
                            <strong className={!site ? 'is-empty' : undefined}>{site || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Usinas</span>
                            <strong className={usinas.length === 0 ? 'is-empty' : undefined}>{usinasLabel}</strong>
                        </Box>
                        <Box>
                            <span>Status</span>
                            <strong className={!statusVeg ? 'is-empty' : undefined}>{statusVeg || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Data da verificação</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dataLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <YardRoundedIcon />
                        <p>
                            A classificação será aplicada às usinas selecionadas e usada como referência operacional de O&M.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/4 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Atualizar Vegetação
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AtualizarVegetacao;
